import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Tooltip,
  InputBase,
  Paper,
  Popper,
} from "@mui/material";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import {
  dateOnlyKey,
  formatDate,
} from "../../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import {
  computeExpirationDate,
  deriveFromExpirationDate,
  getExpirationDaysForDocType,
  getTodayYmd,
} from "../../../../../constants/documents/docTypeExpirationDurations";
import { useIsAcquisitions } from "../../../../../pipelines/PipelineContext";
import {
  VALIDITY_DATE_FIELD_LABEL,
  anchorFieldsFor,
  getClosingProjection,
  getValidityReference,
} from "../../../../../utils/documents/validityReference";
import DateInput from "../../../../../components/inputs/DateInput";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import ConfirmationDialog from "../../../../../components/ui/ConfirmationDialog";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import { slate, status, teal } from "../../../../../theme/colors";
import VerificationUploadSidebar from "../../../verificationDocs/VerificationUploadSidebar";
import { formSx } from "../../../../../theme/formStyles";
import { BORROWER_DOC_GROUPS } from "../../../shared/RecordDocCoveragePanel";
import { isDocHidden } from "../../../../../_src_shared/utils/functions/isDocHidden";

const LINE = "#d8e0ea";

/**
 * One verification figure on an ApplicantCard (Credit auth / Credit report /
 * Background check), editable in place: a date box that writes the
 * borrower's `<dateField>` on pick/blur, and a document box.
 *
 * The document box opens the popover ported from tpo-portal-frontend's
 * RequiredItemsStrip:
 *   • nothing attached → "Upload file", plus "Or assign a file already on
 *     this loan" (this type's loan documents not yet on the applicant);
 *   • a file attached → its name and upload meta, Remove (unlink from the
 *     applicant, after a confirm — the file stays on the loan), Replace
 *     (upload a new one) and View.
 * Uploading, replacing and assigning all continue in
 * VerificationUploadSidebar, where the file can be renamed or take a
 * suggested name before it is filed as `target` and linked to the applicant.
 * Files can also be dropped straight onto the box.
 *
 * The borrower model has no per-verification document field, so "attached"
 * means: associated to the borrower with a matching docType (`files`).
 */
const VerificationField = ({
  label,
  borrower,
  loanId,
  dateField,
  dateMode,
  dateLabel = "Date",
  dateHelp,
  target,
  altTargets = [],
  files = [],
  candidates = [],
}) => {
  const dispatch = useDispatch();
  const { updateBorrower, removeAssociation } = useUnderwritingHook();
  const { enqueueSnackbar } = useSnackbar();
  const { updateLoanDocMetadata, getFileById, associateDocumentWithObjects } =
    useLoanFilesHook();
  const loan = useSelector((s) => s.loanDrilldown);
  const loanDocuments = loan?.loanDocuments;
  const fullName = [borrower?.firstName, borrower?.lastName]
    .filter(Boolean)
    .join(" ");

  const savedDate = dateField ? borrower?.[dateField] || null : null;
  const [draftDate, setDraftDate] = useState(savedDate);

  // What the date means (verificationConfig.js): "effective" starts the doc
  // type's validity window from DOC_TYPE_EXPIRATION_DAYS; "expiration" IS the
  // expiry (photo ID); "plain" has no validity; "none" = no date at all.
  const validityDays = getExpirationDaysForDocType(target?.docType);
  const mode =
    dateMode || (!dateField ? "none" : validityDays ? "effective" : "plain");
  const hasDateField = mode !== "none";
  const expiryOf = (d) =>
    !d
      ? null
      : mode === "expiration"
        ? dateOnlyKey(d)
        : mode === "effective" && validityDays
          ? computeExpirationDate(d, validityDays)
          : null;
  const expiresOn = expiryOf(draftDate);

  // Expired is judged against the loan's validity reference day — today, or
  // the frozen close date on a closed loan — through the shared rule
  // (validityReference.js), never a local "today".
  const isAcquisition = useIsAcquisitions();
  const ref = getValidityReference(loan, { isAcquisition });
  const refYmd = ref.frozen ? dateOnlyKey(ref.closingDate) : getTodayYmd();
  const isExpired = (ymd) => !!ymd && !!refYmd && ymd < refYmd;
  // A LIVE loan is checked twice (CLAUDE.md "Document validity"): today, and
  // the upcoming closing — pre-close: scheduled → expected closing date;
  // acquisition: expected closing date. A closed loan has no projection.
  const projection = getClosingProjection(loan, { isAcquisition });
  const closingYmd = projection ? dateOnlyKey(projection.closingDate) : null;
  const lapsesBeforeClosing = (ymd) =>
    !!ymd && !!closingYmd && !isExpired(ymd) && ymd < closingYmd;
  // How the reference day must be disclosed: a closed loan judges as of its
  // anchor date (pre-close: note → funded → est. closing → expected closing;
  // acquisition: note date only), naming the field; closed with no anchor on
  // file falls back to today and must say so.
  const refNote = ref.frozen
    ? `Judged as of ${formatDate(ref.closingDate, "", "M/d/yy")} (${
        VALIDITY_DATE_FIELD_LABEL[ref.closingField] || ref.closingField
      }) — the loan is closed.`
    : ref.unresolved
      ? `The loan is closed but has no ${anchorFieldsFor({ isAcquisition })
          .map((k) => VALIDITY_DATE_FIELD_LABEL[k] || k)
          .join(" / ")} on file, so this is judged against today.`
      : null;
  const [savingDate, setSavingDate] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);

  // Keep the box in step with the record when it is refreshed from outside
  // (sidebar edit, another user's save).
  useEffect(() => {
    setDraftDate(savedDate);
  }, [savedDate]);

  // Saves the borrower's date — and, for a report type that carries a
  // validity window (credit / background), the attached document's
  // `reportEffectiveDate` too. The two are separate fields with no sync on
  // the backend: the borrower date feeds the card / Key Dates, the document's
  // effective date drives its validity everywhere documents are shown
  // (Documents tab, closing readiness). Writing both keeps them agreeing.
  const commitDate = useCallback(
    (next) => {
      const value = next ?? null;
      if (dateOnlyKey(value) === dateOnlyKey(savedDate)) return;
      setSavingDate(true);
      const datedDoc =
        mode === "effective" || mode === "expiration" ? files[0] : null;
      // The document's own dates, in the shape the Documents tab keeps:
      // an effective date for a report; for an ID (an explicit-expiration
      // type) today + the days to the expiry.
      const docDates =
        mode === "expiration"
          ? deriveFromExpirationDate(dateOnlyKey(value))
          : { reportEffectiveDate: dateOnlyKey(value) };
      updateBorrower({
        loanId,
        borrowerId: borrower?._id,
        data: { [dateField]: value },
        onSuccessFn: (updatedLoan) => {
          dispatch(loanDrilldownSet(updatedLoan));
          if (!datedDoc?._id) {
            setSavingDate(false);
            return;
          }
          updateLoanDocMetadata({
            loanId,
            data: [{ _id: datedDoc._id, ...docDates }],
            onSuccessFn: (d) => {
              if (d?._id) dispatch(loanDrilldownSet(d));
              setSavingDate(false);
            },
            onFailFn: () => setSavingDate(false),
          });
        },
        onFailFn: () => {
          setSavingDate(false);
          setDraftDate(savedDate);
        },
      });
    },
    [
      updateBorrower,
      updateLoanDocMetadata,
      loanId,
      borrower?._id,
      dateField,
      savedDate,
      mode,
      files,
      dispatch,
    ],
  );
  const dateDirty = dateOnlyKey(draftDate) !== dateOnlyKey(savedDate);

  // One file per document type, preferred type first (the config sorts
  // `files`): the Credit Auth / Loan Application box shows BOTH a credit
  // authorization and a loan application when the applicant has each.
  const attached = (() => {
    const seen = new Set();
    return files.filter((d) => {
      const k = String(d?.docType || "");
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  })();
  const current = attached[0] || null;
  const linkedIds = new Set(files.map((f) => String(f?._id)));
  // "Or assign a document already uploaded to this loan": first this item's
  // own type (`candidates`), then — on "Show more" — every other visible
  // borrower document. Files already on the applicant are left out of both.
  const assignPrimary = candidates.filter(
    (d) => d?._id && !isDocHidden(d) && !linkedIds.has(String(d._id)),
  );
  const primaryIds = new Set(assignPrimary.map((d) => String(d._id)));
  const assignOthers = (loanDocuments || []).filter(
    (d) =>
      d?._id &&
      !isDocHidden(d) &&
      BORROWER_DOC_GROUPS.includes(d?.docGroup) &&
      !linkedIds.has(String(d._id)) &&
      !primaryIds.has(String(d._id)),
  );

  /* popover + sidebar ─ ported from tpo-portal-frontend's RequiredItemsStrip:
     the box opens a popover; uploading, replacing or assigning all continue
     in VerificationUploadSidebar (rename + suggested names). */
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [popOpen, setPopOpen] = useState(false);
  const paperRef = useRef(null);

  // Close like a dropdown: any press outside the popover (and outside the
  // card that opened it) closes it, as does Escape. Listened for on the
  // document in the CAPTURE phase — the drilldown cards stop click / mousedown
  // propagation, which is what kept MUI's ClickAwayListener from ever seeing
  // those presses. Presses in the date picker's calendar and in dialogs are
  // portalled elsewhere but still belong to this popover.
  useEffect(() => {
    if (!popOpen) return undefined;
    const onPress = (e) => {
      const target = e.target;
      if (paperRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      if (
        target?.closest?.(
          ".MuiPickersPopper-root, .MuiDialog-root, .MuiPopover-root, .verification-upload-drawer",
        )
      )
        return;
      setPopOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPopOpen(false);
    };
    document.addEventListener("mousedown", onPress, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPress, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [popOpen]);
  const [dragOver, setDragOver] = useState(false);
  const [sidebar, setSidebar] = useState(null); // { mode, files?, doc? }
  // The document Remove & Hide is confirming, or null.
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Set by Replace just before the file picker opens.
  const replacingRef = useRef(null);

  const fullDoc = (doc) =>
    (loanDocuments || []).find((d) => d?._id === doc?._id) || doc;

  const preview = (e) => {
    e?.stopPropagation();
    // Resolve against the loan's list so the preview gets the full doc.
    if (current) dispatch(filePreviewSetData(fullDoc(current)));
  };
  const previewDoc = (doc) => {
    if (doc) dispatch(filePreviewSetData(fullDoc(doc)));
  };

  // `replaceId`: the file being replaced — unlinked once the new one lands.
  const openUpload = (fileList, replaceId = null) => {
    const picked = Array.from(fileList || []).map((file) => ({
      id: uuidv4(),
      file,
      name: file.name,
      file_display_name: file.name,
      size: file.size,
    }));
    if (!picked.length) return;
    setPopOpen(false);
    setSidebar({ mode: "upload", files: picked, replaceId });
  };

  // Assign an existing loan document: link it straight to the applicant —
  // no sidebar (that is only for uploading new files). The popover stays open
  // so the date can be set right after; it re-renders on the attached file.
  //
  // The list offers every borrower document, but this box only shows files of
  // its own type — so a document filed as something else is re-filed as the
  // box's type (`target`) first, or it would be linked yet never appear.
  const openAssign = (doc) => {
    if (!doc?._id || !borrower?._id) return;
    setSavingDoc(true);
    const fail = () => setSavingDoc(false);
    const link = () =>
      associateDocumentWithObjects({
        dealId: loanId,
        loanDocumentsIds: [doc._id],
        borrowerIds: [borrower._id],
        onSuccessFn: (d) => {
          if (d?._id) dispatch(loanDrilldownSet(d));
          setSavingDoc(false);
        },
        onFailFn: fail,
      });
    // Only a document that is NOT already one of this item's accepted types
    // gets re-filed — e.g. a loan application assigned as the credit auth
    // stays a loan application (it carries the authorization).
    const accepted = candidates.some((c) => String(c?._id) === String(doc._id));
    const retype =
      !accepted &&
      target?.docType &&
      (doc.docType !== target.docType || doc.docGroup !== target.docGroup);
    if (!retype) {
      link();
      return;
    }
    updateLoanDocMetadata({
      loanId,
      data: [
        { _id: doc._id, docGroup: target.docGroup, docType: target.docType },
      ],
      onSuccessFn: (d) => {
        if (d?._id) dispatch(loanDrilldownSet(d));
        link();
      },
      onFailFn: fail,
    });
  };

  // POST /underwriting/delete_association answers 200 even when it unlinked
  // nothing: its $pull matches `{ _id: id }` sub-documents, but a borrower's
  // `associations.loanDocuments` holds bare ObjectIds, so the pull never hits
  // (eastview-os underwriting.py delete_object_association). Check the loan it
  // returns rather than trusting the 200, and say so when the file is still
  // linked — a silent "removed" that isn't is worse than an error.
  const stillLinked = (d, docId) =>
    (
      (d?.borrowers || []).find((b) => b?._id === borrower?._id)?.associations
        ?.loanDocuments || []
    )
      .map(String)
      .includes(String(docId));

  const warnNotUnlinked = () =>
    enqueueSnackbar(
      "The file could not be unlinked from this applicant — the server accepted the request but left the link in place. It needs a backend fix.",
      { variant: "error", autoHideDuration: 8000 },
    );

  // Remove & Hide = mark the file hidden (update_loan_doc_metadata), then
  // unlink it from this applicant. Hiding goes first: it is the write that
  // reliably takes the file off the card, while the unlink route can answer
  // 200 without unlinking (see stillLinked above). A hidden file stays on the
  // loan and can be un-hidden from the Documents tab.
  // The date goes with the file: the borrower's date is cleared (null — an
  // `undefined` would be dropped from the JSON body and change nothing) once
  // the unlink has run, so the box reads "Not on file", not a dated empty slot.
  const removeCurrent = () => {
    const doc = confirmRemove;
    if (!doc?._id || !borrower?._id) return;
    // With two files attached (credit auth + loan application) only the last
    // one out takes the date with it.
    const lastOne = attached.length <= 1;
    setSavingDoc(true);
    const close = (d) => {
      if (d?._id) dispatch(loanDrilldownSet(d));
      setSavingDoc(false);
      setConfirmRemove(null);
      if (lastOne) setPopOpen(false);
    };
    const finish = (d) => {
      if (d?._id) dispatch(loanDrilldownSet(d));
      if (!lastOne || !dateField || !savedDate) {
        close(null);
        return;
      }
      updateBorrower({
        loanId,
        borrowerId: borrower._id,
        data: { [dateField]: null },
        onSuccessFn: (updated) => {
          setDraftDate(null);
          close(updated);
        },
        onFailFn: () => close(null),
      });
    };
    updateLoanDocMetadata({
      loanId,
      data: [{ _id: doc._id, isHidden: true }],
      onSuccessFn: (hidden) => {
        if (hidden?._id) dispatch(loanDrilldownSet(hidden));
        removeAssociation({
          dealId: loanId,
          associationTypeName: "borrowers",
          associationTypeId: borrower._id,
          associationToDeleteName: "loanDocuments",
          associationIdToDelete: doc._id,
          onSuccessFn: finish,
          // Hidden already took it off the card; a failed unlink is not worth
          // undoing that for.
          onFailFn: () => finish(null),
        });
      },
      onFailFn: () => setSavingDoc(false),
    });
  };

  const download = (which = current) => {
    const doc = which ? fullDoc(which) : null;
    if (!doc?.file_storage_name) return;
    getFileById({
      loanId,
      file_storage_name: doc.file_storage_name,
      fileDownloadName: doc.file_display_name || doc.name,
      fileDownload: true,
    });
  };

  // The card's state, worst first: no file → "Not on file"; a file but no
  // date → "Needs date"; both → "Complete". Amber until complete.
  const hasFile = !!current;
  const hasDate = !hasDateField || !!savedDate;
  const savedExpiry = expiryOf(savedDate);
  const expired = hasFile && hasDate && isExpired(savedExpiry);
  // Worst first: no file → "Not on file"; no date → "Needs date"; past its
  // validity → "Expired" (label turns red); else "Complete".
  const cardState = !hasFile
    ? "missing"
    : !hasDate
      ? "needsDate"
      : expired
        ? "expired"
        : "complete";
  const warn = cardState === "missing" || cardState === "needsDate";

  return (
    <Box
      sx={{ minWidth: 0 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Title — above the box, outside it (original 10px uppercase label;
          green + check when complete, red + lapsed-calendar when expired,
          amber + dot while something is missing). */}
      <Flx ac sx={{ minWidth: 0, mb: "6px" }}>
        <Flx ac sx={{ gap: "6px", minWidth: 0 }}>
          {warn && (
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: status.warning.solid,
                flexShrink: 0,
              }}
            />
          )}
          {/* Every property spelled out so the label renders exactly as
              it did before, whatever the button around it inherits. */}
          {/* State icon to the LEFT of the title. */}
          {cardState === "complete" && (
            <CheckCircleRounded
              aria-label="Complete"
              sx={{
                fontSize: 12,
                display: "block",
                color: status.success.solid,
                flexShrink: 0,
              }}
            />
          )}
          {/* Expired: a lapsed-calendar mark in the checkmark's place. */}
          {cardState === "expired" && (
            <EventBusyRounded
              aria-label="Expired"
              sx={{
                fontSize: 12,
                display: "block",
                color: status.error.solid,
                flexShrink: 0,
              }}
            />
          )}
          <Txt sx={LABEL_SX(cardState)}>{label}</Txt>
          {(savingDate || savingDoc) && (
            <CircularProgress
              size={10}
              thickness={5}
              sx={{ color: slate[400] }}
            />
          )}
        </Flx>
      </Flx>

      {/* The card — one click target that opens the popover (date + file).
          Files can also be dropped straight onto it. */}
      <ButtonBase
        ref={anchorRef}
        component="div"
        aria-haspopup="dialog"
        aria-expanded={popOpen}
        aria-label={`${label}: ${
          {
            missing: "not on file",
            needsDate: "needs date",
            complete: "complete",
          }[cardState]
        }`}
        onClick={() => setPopOpen((v) => !v)}
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          setDragOver(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (!dragDepth.current) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragOver(false);
          openUpload(e.dataTransfer?.files);
        }}
        // The box: flush with the figures box above (no negative margin), the
        // file and date inside; a light amber wash until the field is complete.
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "10px",
          padding: "10px 12px",
          textAlign: "left",
          borderRadius: "8px",
          border: `1px ${dragOver ? "dashed" : "solid"} ${
            dragOver
              ? teal[600]
              : warn
                ? popOpen
                  ? status.warning.solid
                  : "#FDE68A"
                : popOpen
                  ? slate[300]
                  : slate[200]
          }`,
          background: dragOver
            ? teal[50]
            : warn
              ? status.warning.bg
              : "transparent",
          transition: "border-color .12s, background-color .12s",
          "&:hover": {
            borderColor: warn ? status.warning.solid : slate[300],
          },
        }}
      >
        {/* Document(s) — one line per attached file, stacked. */}
        {(hasFile ? attached : [null]).map((doc, i) => (
          <Flx
            key={doc?._id || `none-${i}`}
            ac
            sx={{ gap: ROW_GAP, minWidth: 0 }}
          >
            <InsertDriveFileOutlined
              sx={{
                fontSize: ROW_ICON,
                display: "block",
                flexShrink: 0,
                color: doc ? slate[400] : status.warning.fg,
              }}
            />
            <Txt
              noWrap
              title={doc ? docName(doc) : undefined}
              sx={{
                fontSize: ROW_FONT,
                lineHeight: ROW_LINE,
                minWidth: 0,
                color: doc ? slate[800] : status.warning.fg,
                fontWeight: doc ? 400 : 600,
              }}
            >
              {doc
                ? docName(doc)
                : dragOver
                  ? "Drop to upload"
                  : "Attach document"}
            </Txt>
          </Flx>
        ))}

        {/* Date — none for a document-only item (loan application). */}
        {hasDateField && (
          <Flx ac sx={{ gap: ROW_GAP, minWidth: 0 }}>
            <CalendarTodayOutlined
              sx={{
                fontSize: ROW_ICON - 1,
                display: "block",
                flexShrink: 0,
                color: hasDate ? slate[400] : status.warning.fg,
              }}
            />
            {hasDate ? (
              <Txt
                noWrap
                sx={{
                  fontSize: ROW_FONT,
                  lineHeight: ROW_LINE,
                  color: slate[800],
                  minWidth: 0,
                }}
              >
                {formatDate(savedDate, "—", "MMM d, yyyy")}
                {savedExpiry && (
                  <Box
                    component="span"
                    sx={{
                      color: expired
                        ? status.error.fg
                        : lapsesBeforeClosing(savedExpiry)
                          ? status.warning.fg
                          : slate[400],
                      fontWeight:
                        expired || lapsesBeforeClosing(savedExpiry) ? 600 : 400,
                    }}
                  >
                    {expired
                      ? ` · expired ${formatDate(savedExpiry, "", "MMM d")}`
                      : lapsesBeforeClosing(savedExpiry)
                        ? ` · exp ${formatDate(savedExpiry, "", "MMM d")}, before closing`
                        : ` · exp ${formatDate(savedExpiry, "", "MMM d")}`}
                  </Box>
                )}
              </Txt>
            ) : (
              <Txt
                noWrap
                sx={{
                  fontSize: ROW_FONT,
                  lineHeight: ROW_LINE,
                  fontWeight: 600,
                  color: status.warning.fg,
                }}
              >
                {`Add ${dateLabel.toLowerCase()}`}
              </Txt>
            )}
          </Flx>
        )}
      </ButtonBase>

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          openUpload(e.target.files, replacingRef.current);
          replacingRef.current = null;
          e.target.value = "";
        }}
      />

      <Popper
        open={popOpen && !!anchorRef.current}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        // Above the drilldown's docked sidebars (ResizeableSidebar sits at
        // 13000) so it opens in front of them; just under the date picker's
        // calendar (13500) so that still opens on top of the popover.
        sx={{ zIndex: POPOVER_Z }}
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
      >
        <Paper
          ref={paperRef}
          role="dialog"
          aria-label={label}
          elevation={0}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            width: POPOVER_WIDTH,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: "12px",
            border: `1px solid ${slate[200]}`,
            boxShadow: "0 12px 32px rgba(15,23,42,.16)",
            overflow: "hidden",
          }}
        >
          {/* Header — the field, and where it stands. */}
          <Flx ac jb g={1} sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
            <Txt sx={{ fontSize: 14, fontWeight: 600, color: slate[900] }}>
              {label}
            </Txt>
            <StatusPill state={cardState} />
          </Flx>

          <Box sx={{ px: 2, pb: 1.75 }}>
            {current ? (
              /* The assigned document. */
              <Flx column g={0.75}>
                {attached.map((doc) => (
                  <AttachedDocCard
                    key={doc._id}
                    doc={doc}
                    onOpen={() => {
                      setPopOpen(false);
                      previewDoc(doc);
                    }}
                    // With more than one file each card carries its own
                    // download / remove; the footer is for a single file.
                    actions={
                      attached.length > 1 ? (
                        <>
                          <Tooltip title="Download">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Download ${docName(doc)}`}
                                disabled={!fullDoc(doc)?.file_storage_name}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  download(doc);
                                }}
                                sx={{ p: "4px", color: slate[500] }}
                              >
                                <FileDownloadOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove & Hide">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Remove & hide ${docName(doc)}`}
                                disabled={savingDoc}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmRemove(doc);
                                }}
                                sx={{ p: "4px", color: status.error.fg }}
                              >
                                <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : null
                    }
                  />
                ))}
              </Flx>
            ) : (
              /* Nothing yet: upload, or assign one already on the loan. */
              <>
                <Txt
                  component="div"
                  sx={{ fontSize: 12.5, color: slate[600], mb: "6px" }}
                >
                  Document
                </Txt>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  startIcon={<FileUploadOutlined />}
                  onClick={() => {
                    replacingRef.current = null;
                    inputRef.current?.click();
                  }}
                  sx={{
                    height: 36,
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: "8px",
                    textTransform: "none",
                  }}
                >
                  Upload file
                </Button>
                {assignPrimary.length + assignOthers.length > 0 && (
                  <AssignList
                    primary={assignPrimary}
                    others={assignOthers}
                    onAssign={openAssign}
                  />
                )}
              </>
            )}
          </Box>

          {/* Date — always at the bottom; none for a document-only item. */}
          {hasDateField && (
            <>
              <Box
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 1.75,
                  borderTop: `1px solid ${slate[200]}`,
                }}
              >
                <Txt
                  component="div"
                  sx={{ fontSize: 12.5, color: slate[600], mb: "6px" }}
                >
                  {dateLabel}
                  {current && !hasDate && (
                    <Box
                      component="span"
                      sx={{ color: status.warning.fg, fontWeight: 500 }}
                    >
                      {" · required to complete"}
                    </Box>
                  )}
                </Txt>
                <DateInput
                  value={draftDate}
                  onChange={setDraftDate}
                  disabled={savingDate}
                  size="small"
                  fullWidth
                  placeholder="—"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 36,
                      fontSize: 13,
                      borderRadius: "8px",
                      background:
                        current && !hasDate ? status.warning.bg : "#ffffff",
                    },
                    "& .MuiInputBase-input": { py: 0, px: 1.25 },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor:
                        current && !hasDate ? status.warning.solid : LINE,
                    },
                  }}
                />
                {(expiresOn || (!current && dateHelp)) && (
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 12,
                      mt: "6px",
                      color:
                        expiresOn && isExpired(expiresOn)
                          ? status.error.fg
                          : slate[500],
                    }}
                  >
                    {expiresOn
                      ? `${isExpired(expiresOn) ? "Expired" : "Valid until"} ${formatDate(expiresOn, "", "MMM d, yyyy")}${
                          mode === "effective" ? ` (${validityDays} days)` : ""
                        }`
                      : dateHelp}
                  </Txt>
                )}
                {expiresOn && lapsesBeforeClosing(expiresOn) && (
                  <Txt
                    component="div"
                    sx={{ fontSize: 12, mt: "4px", color: status.warning.fg }}
                  >
                    {`Expires before closing — ${
                      VALIDITY_DATE_FIELD_LABEL[projection.closingField] ||
                      projection.closingField
                    } ${formatDate(projection.closingDate, "", "MMM d, yyyy")}.`}
                  </Txt>
                )}
                {expiresOn && refNote && (
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 11.5,
                      mt: "4px",
                      color: ref.unresolved ? status.warning.fg : slate[500],
                    }}
                  >
                    {refNote}
                  </Txt>
                )}
                {/* Save / Cancel — only once the date differs from what's saved. */}
                {dateDirty && (
                  <Flx
                    ac
                    g={0.75}
                    sx={{ justifyContent: "flex-end", mt: 1.25 }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="gray"
                      disabled={savingDate}
                      onClick={() => setDraftDate(savedDate)}
                      sx={POP_BTN_SX}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      disabled={savingDate}
                      onClick={() => commitDate(draftDate)}
                      startIcon={
                        savingDate ? (
                          <CircularProgress size={12} color="inherit" />
                        ) : null
                      }
                      sx={POP_BTN_SX}
                    >
                      {savingDate ? "Saving…" : "Save"}
                    </Button>
                  </Flx>
                )}
              </Box>
            </>
          )}

          {/* Footer — the assigned document's actions, in this order:
              Remove | Download | Preview. Compact so they fit one row. */}
          {attached.length === 1 && (
            <Flx
              ac
              g={0.5}
              sx={{
                px: 2,
                py: 1,
                borderTop: `1px solid ${slate[200]}`,
                backgroundColor: slate[50],
                flexWrap: "nowrap",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={savingDoc}
                onClick={() => setConfirmRemove(current)}
                startIcon={<DeleteOutlineRounded sx={FOOT_ICON_SX} />}
                sx={FOOT_BTN_SX}
              >
                Remove &amp; Hide
              </Button>
              <Box sx={{ flex: 1, minWidth: 4 }} />
              <Button
                size="small"
                variant="outlined"
                color="gray"
                disabled={!fullDoc(current)?.file_storage_name}
                onClick={() => download(current)}
                startIcon={<FileDownloadOutlined sx={FOOT_ICON_SX} />}
                sx={FOOT_BTN_SX}
              >
                Download
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="gray"
                onClick={() => {
                  setPopOpen(false);
                  preview();
                }}
                startIcon={<VisibilityOutlined sx={FOOT_ICON_SX} />}
                sx={FOOT_BTN_SX}
              >
                Preview
              </Button>
            </Flx>
          )}
        </Paper>
      </Popper>

      <VerificationUploadSidebar
        open={!!sidebar}
        mode={sidebar?.mode}
        files={sidebar?.files}
        doc={sidebar?.doc}
        label={label}
        target={target}
        altTargets={altTargets}
        borrower={borrower}
        loan={loan}
        onClose={() => setSidebar(null)}
        onDone={() => {
          // Replace = the new file in, the old one unlinked (it stays on the loan).
          const oldId = sidebar?.replaceId;
          if (!oldId || !borrower?._id) return;
          removeAssociation({
            dealId: loanId,
            associationTypeName: "borrowers",
            associationTypeId: borrower._id,
            associationToDeleteName: "loanDocuments",
            associationIdToDelete: oldId,
            onSuccessFn: (d) => {
              if (d?._id) dispatch(loanDrilldownSet(d));
              if (stillLinked(d, oldId)) warnNotUnlinked();
            },
          });
        }}
      />

      <Box
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ConfirmationDialog
          sx={{ zIndex: OVERLAY_Z }}
          open={!!confirmRemove}
          title={`Remove & hide ${label.toLowerCase()}`}
          message={`${confirmRemove ? docName(confirmRemove) : "This file"} is unlinked from ${fullName || "this applicant"} and hidden${attached.length <= 1 && savedDate && dateLabel ? `, and the ${dateLabel.toLowerCase()} is cleared` : ""}. It stays on the loan, and can be shown again from the Documents tab.`}
          confirmText="Remove & Hide"
          destructive
          loading={savingDoc}
          onConfirm={removeCurrent}
          onCancel={() => setConfirmRemove(null)}
        />
      </Box>
    </Box>
  );
};

/* ── popover bodies (tpo-portal-frontend RequiredItemsStrip) ─────────── */

const POPOVER_WIDTH = 460;

// Layering over the drilldown's docked sidebars (ResizeableSidebar: 13000).
const POPOVER_Z = 13400;
const OVERLAY_Z = 13600; // the Remove & Hide confirm

const ELLIPSIS = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Card rows: 12px text, small icons, a tight gap.
const ROW_FONT = 12;
const ROW_ICON = 14;
const ROW_GAP = "6px";
// Row text line box = the icon's box, so text and icon center exactly.
const ROW_LINE = `${ROW_ICON}px`;

// The field label, exactly as it rendered before the card (font, size,
// weight, line-height, tracking) — only the color changes with the state.
const LABEL_SX = (state) => ({
  m: 0,
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: 10,
  lineHeight: 1.55,
  fontWeight: state === "complete" ? 500 : 600,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color:
    state === "complete"
      ? status.success.fg
      : state === "expired"
        ? status.error.fg
        : status.warning.fg,
});

const docName = (doc) =>
  doc?.file_display_name || doc?.name || "Untitled document";

const PdfIcon = ({ name }) => {
  const ext = (String(name || "").match(/\.([A-Za-z0-9]{1,5})$/)?.[1] || "file")
    .slice(0, 4)
    .toUpperCase();
  return (
    <Flx
      ac
      jc
      sx={{
        width: 28,
        height: 32,
        flexShrink: 0,
        borderRadius: "4px",
        border: `1px solid ${slate[200]}`,
        fontSize: 8.5,
        fontWeight: 800,
        color: ext === "PDF" ? status.error.fg : slate[600],
        backgroundColor: "#fff",
      }}
    >
      {ext}
    </Flx>
  );
};

// The footer buttons: small type, tight padding, no wrapping — four of them
// share a 460px popover.
const FOOT_BTN_SX = {
  minWidth: 0,
  height: 28,
  px: 1,
  fontSize: 11.5,
  fontWeight: 600,
  borderRadius: "6px",
  textTransform: "none",
  whiteSpace: "nowrap",
  flexShrink: 0,
  backgroundColor: "#fff",
  "& .MuiButton-startIcon": { mr: "4px", ml: 0 },
};
const FOOT_ICON_SX = { fontSize: "14px !important" };

const POP_BTN_SX = {
  minWidth: 0,
  height: 30,
  px: 1.25,
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: "7px",
  textTransform: "none",
};

/** "Not on file" / "Incomplete" / "Complete" — the popover header's state. */
const StatusPill = ({ state }) => {
  if (state === "expired") {
    return (
      <Box
        component="span"
        sx={{
          px: "8px",
          py: "1px",
          borderRadius: "999px",
          fontSize: 11.5,
          fontWeight: 600,
          whiteSpace: "nowrap",
          color: status.error.fg,
          backgroundColor: status.error.bg,
          border: "1px solid #FECACA",
        }}
      >
        Expired
      </Box>
    );
  }
  const ok = state === "complete";
  const text = {
    missing: "Not on file",
    needsDate: "Incomplete",
    complete: "Complete",
  }[state];
  return (
    <Box
      component="span"
      sx={{
        px: "8px",
        py: "1px",
        borderRadius: "999px",
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: ok ? status.success.fg : status.warning.fg,
        backgroundColor: ok ? status.success.bg : status.warning.bg,
        border: `1px solid ${ok ? "#A7F3D0" : "#FDE68A"}`,
      }}
    >
      {text}
    </Box>
  );
};

/** The assigned document: name, and the group › type it is filed under. */
const AttachedDocCard = ({ doc, onOpen, actions }) => (
  <ButtonBase
    onClick={onOpen}
    component="div"
    sx={{
      width: "100%",
      justifyContent: "flex-start",
      gap: 1.25,
      px: 1.5,
      py: 1.25,
      textAlign: "left",
      borderRadius: "10px",
      border: `1px solid ${slate[200]}`,
      "&:hover": { backgroundColor: slate[50] },
    }}
  >
    <PdfIcon name={docName(doc)} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Txt
        component="div"
        title={docName(doc)}
        sx={{ ...ELLIPSIS, fontSize: 13.5, fontWeight: 600, color: slate[900] }}
      >
        {docName(doc)}
      </Txt>
      <Txt
        component="div"
        sx={{ ...ELLIPSIS, fontSize: 12, color: slate[500] }}
      >
        {[
          [doc?.docGroup, doc?.docType].filter(Boolean).join(" › "),
          doc?.uploadDate
            ? `Uploaded ${formatDateTime(doc.uploadDate, "M/d/yy")}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || "On this loan"}
      </Txt>
    </Box>
    {actions}
    {/* The card previews on click; the eye says so. */}
    <VisibilityOutlined
      aria-label="Preview"
      sx={{ fontSize: 18, color: slate[500], flexShrink: 0 }}
    />
  </ButtonBase>
);

const newestFirst = (a, b) =>
  String(b?.uploadDate || "").localeCompare(String(a?.uploadDate || ""));

/** Group documents by docType (the tree's parent rows), biggest first. */
const byDocType = (docs) => {
  const map = new Map();
  docs.forEach((d) => {
    const type = d?.docType || "Uncategorized";
    if (!map.has(type)) map.set(type, []);
    map.get(type).push(d);
  });
  return [...map.entries()]
    .map(([type, list]) => ({ type, docs: [...list].sort(newestFirst) }))
    .sort(
      (a, b) => b.docs.length - a.docs.length || a.type.localeCompare(b.type),
    );
};

/**
 * One document type as a tree: the type row (dot · name · count · chevron),
 * and its files beneath, indented against a vertical guide line — the same
 * shape the Assigned Documents panel uses for a type and its files.
 */
const TypeTree = ({ type, docs, open, onToggle, onAssign }) => (
  <Box>
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        width: "100%",
        justifyContent: "space-between",
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: "6px",
        "&:hover": { backgroundColor: slate[50] },
      }}
    >
      <Flx ac sx={{ gap: "8px", minWidth: 0 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: teal[600],
            flexShrink: 0,
          }}
        />
        <Txt
          sx={{
            ...ELLIPSIS,
            fontSize: 11.5,
            fontWeight: 600,
            color: slate[800],
          }}
        >
          {type}
        </Txt>
      </Flx>
      <Flx ac sx={{ gap: "6px", flexShrink: 0 }}>
        <Box
          component="span"
          sx={{
            px: "6px",
            borderRadius: "999px",
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1.7,
            color: teal[700],
            backgroundColor: teal[50],
          }}
        >
          {docs.length}
        </Box>
        <ExpandMoreRounded
          sx={{
            fontSize: 16,
            color: slate[400],
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        />
      </Flx>
    </ButtonBase>
    {open && (
      <Box
        sx={{
          ml: "12px",
          pl: 1,
          borderLeft: `1px solid ${slate[200]}`,
          mb: 0.5,
        }}
      >
        {docs.map((doc) => (
          <ButtonBase
            key={doc._id}
            onClick={() => onAssign(doc)}
            title={docName(doc)}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              gap: 1,
              px: 0.75,
              py: 0.5,
              borderRadius: "6px",
              textAlign: "left",
              "&:hover": { backgroundColor: slate[50] },
            }}
          >
            <Flx ac sx={{ gap: "8px", minWidth: 0 }}>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: slate[400],
                  flexShrink: 0,
                }}
              />
              <Txt sx={{ ...ELLIPSIS, fontSize: 11, color: slate[900] }}>
                {docName(doc)}
              </Txt>
            </Flx>
            {doc?.uploadDate && (
              <Txt
                sx={{ fontSize: 10, color: slate[400], whiteSpace: "nowrap" }}
              >
                {formatDateTime(doc.uploadDate, "M/d/yy")}
              </Txt>
            )}
          </ButtonBase>
        ))}
      </Box>
    )}
  </Box>
);

/**
 * "Or assign a document already uploaded to this loan": first only the loan's
 * documents of THIS item's type, then — on "Show more" — the rest of the
 * borrower documents. Both lists are trees by document type with a search
 * over name and type. Picking a file assigns it to the applicant.
 */
const AssignList = ({ primary, others, onAssign }) => {
  const [query, setQuery] = useState("");
  const [showMore, setShowMore] = useState(primary.length === 0);
  const [folded, setFolded] = useState(() => new Set());

  const q = query.trim().toLowerCase();
  const hit = (d) =>
    !q ||
    [docName(d), d?.docType, d?.docGroup]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(q));
  const primaryTree = byDocType(primary.filter(hit));
  const otherTree = byDocType(others.filter(hit));
  // A search looks through everything, and opens every type it matches in.
  const showOthers = showMore || !!q;
  const toggle = (key) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const isOpen = (key) => !!q || !folded.has(key);
  const nothing = !primaryTree.length && (!showOthers || !otherTree.length);

  return (
    <>
      {/* Divider with the instruction set into it. */}
      <Flx ac g={1} sx={{ my: 1.25 }}>
        <Box sx={{ flex: 1, borderTop: `1px solid ${slate[200]}` }} />
        <Txt sx={{ fontSize: 11, color: slate[500], whiteSpace: "nowrap" }}>
          or assign a document already uploaded to this loan
        </Txt>
        <Box sx={{ flex: 1, borderTop: `1px solid ${slate[200]}` }} />
      </Flx>
      <Box
        sx={{
          border: `1px solid ${slate[200]}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Search */}
        <Box sx={{ px: 1, py: 0.75, borderBottom: `1px solid ${slate[200]}` }}>
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents"
            inputProps={{ "aria-label": "Search documents" }}
            startAdornment={
              <SearchRounded
                sx={{ fontSize: 14, color: slate[400], mr: 0.5 }}
              />
            }
            sx={{
              width: "100%",
              height: 26,
              px: 0.75,
              fontSize: 11,
              borderRadius: "6px",
              border: `1px solid ${slate[200]}`,
              backgroundColor: "#fff",
              "&.Mui-focused": { borderColor: teal[600] },
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 220, overflowY: "auto", p: 0.5 }}>
          {primaryTree.map((g) => (
            <TypeTree
              key={`p:${g.type}`}
              type={g.type}
              docs={g.docs}
              open={isOpen(`p:${g.type}`)}
              onToggle={() => toggle(`p:${g.type}`)}
              onAssign={onAssign}
            />
          ))}

          {!showOthers && others.length > 0 && (
            <ButtonBase
              onClick={() => setShowMore(true)}
              sx={{
                ...formSx.link,
                fontSize: 11,
                px: 1,
                py: 0.75,
                borderRadius: "6px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {`Show more borrower documents (${others.length})`}
            </ButtonBase>
          )}

          {showOthers && otherTree.length > 0 && (
            <>
              <Flx
                ac
                jb
                g={1}
                sx={{
                  px: 1,
                  pt: primaryTree.length ? 1 : 0.5,
                  pb: 0.25,
                  borderTop: primaryTree.length
                    ? `1px solid ${slate[200]}`
                    : "none",
                  mt: primaryTree.length ? 0.5 : 0,
                }}
              >
                <Txt
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: slate[500],
                  }}
                >
                  Other borrower documents
                </Txt>
                {showMore && primary.length > 0 && !q && (
                  <ButtonBase
                    onClick={() => setShowMore(false)}
                    sx={{ ...formSx.link, fontSize: 10.5 }}
                  >
                    Show less
                  </ButtonBase>
                )}
              </Flx>
              {otherTree.map((g) => (
                <TypeTree
                  key={`o:${g.type}`}
                  type={g.type}
                  docs={g.docs}
                  open={isOpen(`o:${g.type}`)}
                  onToggle={() => toggle(`o:${g.type}`)}
                  onAssign={onAssign}
                />
              ))}
            </>
          )}

          {nothing && (
            <Txt
              component="div"
              sx={{ fontSize: 11, color: slate[500], px: 1, py: 1 }}
            >
              {q
                ? `No documents match “${query.trim()}”.`
                : "No documents of this type on the loan yet."}
            </Txt>
          )}
        </Box>
      </Box>
    </>
  );
};

VerificationField.propTypes = {
  label: PropTypes.string.isRequired,
  borrower: PropTypes.object,
  loanId: PropTypes.string,
  /** The borrower field the date box writes, e.g. "creditReportDate". */
  /** The borrower field the date writes; omit for a document-only item. */
  dateField: PropTypes.string,
  /** "effective" | "expiration" | "plain" | "none" — see verificationConfig.js. */
  dateMode: PropTypes.oneOf(["effective", "expiration", "plain", "none"]),
  /** What the date IS, e.g. "Pull date" / "Auth date". */
  dateLabel: PropTypes.string,
  /** Tooltip spelling out the date rule. */
  dateHelp: PropTypes.string,
  /** `{ docGroup, docType }` an uploaded / assigned file is filed under. */
  target: PropTypes.shape({
    docGroup: PropTypes.string,
    docType: PropTypes.string,
  }),
  /** Other `{ docGroup, docType }`s that satisfy the item (offered as name
   *  suggestions on upload — e.g. Loan Application for the credit auth). */
  altTargets: PropTypes.arrayOf(
    PropTypes.shape({ docGroup: PropTypes.string, docType: PropTypes.string }),
  ),
  /** The applicant's associated files of this verification's type. */
  files: PropTypes.array,
  /** Every loan document of this type. */
  candidates: PropTypes.array,
};

export default VerificationField;
